import { ParsedUrlQuery } from 'querystring'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import styled, { createGlobalStyle } from 'styled-components'
import { format, parse } from 'date-fns'
import { Form } from 'formik'
import toNumber from 'lodash/toNumber'
import { useDispatch } from 'react-redux'

import { Plan } from '@/types'
import { Session } from '@/models/session'
import { breakpoints, colors } from '@/styles'
import handleSession from '@/utils/handle-session'
import useCurrentSession from '@/hooks/use-current-session'
import {
  isDowngrade,
  isValidPlan,
  parsePlan,
  PlanName
} from '@/features/Pricing/pricing-plans'

import {
  AccountFormProvider,
  AccountSection,
  AccountSectionView,
  BillingSection,
  PaymentFormProvider,
  SummarySection,
  TitleSection,
  useActivePlan
} from '.'

const HideScroll = createGlobalStyle`
  html {
    overflow-y: hidden;
  }
  @media (max-width: ${breakpoints.desktop}) {
    html {
      overflow-y: scroll;
    }
  }
`

// We need do so some math here in order
// to keep the left column fixed while right column scrolls.
// Instead of centering we use vw to calculate the left margin
// of the container, while adding the equivalent padding so that
// the scroll bar sticks to the right.
const PageWrapper = styled.div`
  --container: 1440px;
  --form: 660px;

  display: flex;
  min-height: 100vh;

  max-width: calc(var(--container) + ((100vw - var(--container)) / 2));
  margin-left: max((100vw - var(--container)) / 2, 0px);

  @media (max-width: ${breakpoints.desktop}) {
    margin-left: 0;
    max-width: unset;
    flex-direction: column;
  }
`

const FormSection = styled.section`
  width: 100%;
  max-width: max(var(--form) + (100vw - var(--container)) / 2, var(--form));
  min-width: max(var(--form) + (100vw - var(--container)) / 2, var(--form));
  background: ${colors.blue96};
  padding: 48px max(52px + (100vw - var(--container)) / 2, 52px) 96px 52px;
  height: 100vh;
  overflow-y: auto;

  @media (max-width: ${breakpoints.desktop}) {
    height: 100%;
    overflow-y: visible;
    flex: 1 0 auto;
    min-width: unset;
    max-width: unset;
    display: flex;
    align-items: center;
    flex-direction: column;
    padding: 32px 32px 96px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 16px 16px 64px;
  }
`

const StyledForm = styled(Form)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const HeaderForm = styled.div`
  width: 100%;

  @media (max-width: ${breakpoints.desktop}) {
    max-width: 660px;
  }
`

const AccountForm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @media (max-width: ${breakpoints.desktop}) {
    max-width: 660px;
  }
`

const PayForm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @media (max-width: ${breakpoints.desktop}) {
    max-width: 660px;
  }
`

const formatDate = (date: Date) => (date ? format(date, 'MMM d, yyyy') : '')

type Props = {
  pagePath?: string
  pageId?: string
  customTitles?: Record<string, string>
}

type UrlProps = {
  isRenter: boolean
  accountType?: string
  boughtAccountType?: string
  loading?: boolean
  intendedPlan?: string
  isLoggedIn: boolean
  registrationComplete: boolean
  isDowngradeUpgrade: boolean
  pagePath?: string
}

const UrlUpdater: React.FC<UrlProps> = ({
  isRenter,
  accountType,
  boughtAccountType,
  intendedPlan,
  isLoggedIn,
  registrationComplete,
  isDowngradeUpgrade,
  loading,
  pagePath
}) => {
  const router = useRouter()
  const validQuery = isValidPlan(accountType)

  const redirectToPricing =
    (isLoggedIn && registrationComplete) ||
    ([boughtAccountType, 'essentials'].includes(accountType) &&
      isDowngradeUpgrade &&
      !loading)
  const redirectToEssentials = !validQuery

  useEffect(() => {
    if (!router.isReady) return
    if (isRenter) {
      router.replace('/')
    } else if (redirectToPricing) {
      router.replace('/pricing')
    } else if (redirectToEssentials) {
      router.replace(
        { pathname: pagePath, query: { accountType: 'essentials' } },
        undefined,
        { shallow: true }
      )
    }
  }, [
    router,
    isRenter,
    intendedPlan,
    redirectToPricing,
    redirectToEssentials,
    pagePath
  ])
  return null
}

const getQuery = (query: ParsedUrlQuery['accountType']) => {
  if (!query) return ''
  if (Array.isArray(query)) return ''
  return query
}

const isLoggedIn = (profile?: Session): profile is Session =>
  !!profile?.user_type

const getStartDate = (
  currentAccountType: PlanName,
  accountType: PlanName,
  isDowngradeUpgrade: boolean,
  activePlan?: Plan
): string => {
  const downgrade = isDowngrade(currentAccountType, accountType)
  const now = new Date()
  if (isDowngradeUpgrade) {
    if (!activePlan) return '--- --, ----'
    const value = downgrade ? activePlan?.chargedThroughDate : now
    if (typeof value === 'string') {
      try {
        const parsed = parse(value, 'yyyy-MM-dd', now)
        return formatDate(parsed)
      } catch (e) {
        // fall-through
      }
    }
  }
  return formatDate(now)
}

const RegisterForm: React.FC<Props> = ({
  pagePath = '/register',
  pageId = 'register',
  customTitles
}) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const plan = getQuery(router.query?.accountType).toLowerCase()
  const listingId = getQuery(router.query?.listingId)
  const accountType = isValidPlan(plan) ? plan : 'essentials'
  const isEssentialsPlan = plan === 'essentials'
  const profile = useCurrentSession()

  const basePlanName = profile?.company_info?.base_plan_name

  const [planType] = parsePlan(basePlanName)
  const isSpecialPlan = planType === 'special'
  const currentAccountType = planType !== 'special' ? planType : 'essentials'

  const isRenter = profile?.user_type === 'tenant'
  const intendedPlan = profile?.intended_plan
  const loggedIn = isLoggedIn(profile)

  const userId = profile?.id
  const billingUserId = profile?.company_info?.billing_user_id
  const isBillingUser = toNumber(userId) === toNumber(billingUserId)
  const registrationComplete = isSpecialPlan || !isBillingUser

  const { active, next, loading } = useActivePlan()
  const pendingPlanName = next?.plan
  const [pendingAccountType] = parsePlan(pendingPlanName)
  const boughtPlanType = !pendingPlanName
    ? currentAccountType
    : pendingAccountType

  const subscriptionId = active?.subscriptionId
  const isDowngradeUpgrade = !intendedPlan && loggedIn
  const date = getStartDate(
    currentAccountType,
    accountType,
    isDowngradeUpgrade,
    active
  )

  useEffect(() => {
    const getSession = async () => {
      await handleSession(dispatch.session.loadSession())
    }
    if (listingId && !loggedIn) {
      getSession()
    }
  }, [listingId])

  if (!router.isReady) return null
  return (
    <>
      <HideScroll />
      <PageWrapper>
        <TitleSection accountType={accountType} customTitles={customTitles} />
        <UrlUpdater
          isRenter={isRenter}
          accountType={plan}
          boughtAccountType={boughtPlanType}
          intendedPlan={intendedPlan}
          isLoggedIn={loggedIn}
          registrationComplete={registrationComplete}
          isDowngradeUpgrade={isDowngradeUpgrade}
          loading={loading}
          pagePath={pagePath}
        />
        <FormSection>
          {!isLoggedIn(profile) ? (
            <AccountFormProvider accountType={plan} pageId={pageId}>
              <StyledForm noValidate>
                <AccountForm>
                  <AccountSection pageId={pageId} />
                </AccountForm>
              </StyledForm>
            </AccountFormProvider>
          ) : (
            <AccountForm>
              <AccountSectionView profile={profile} />
            </AccountForm>
          )}
          {isLoggedIn(profile) && (
            <PaymentFormProvider
              accountType={accountType}
              subscriptionId={subscriptionId}
              changePlanRequest={isDowngradeUpgrade}
              listingId={listingId}
            >
              {accountType !== 'essentials' && (
                <StyledForm noValidate>
                  <HeaderForm>
                    {!isEssentialsPlan && (
                      <SummarySection
                        date={date}
                        subscriptionId={subscriptionId}
                      />
                    )}
                  </HeaderForm>
                  <PayForm>
                    <BillingSection />
                  </PayForm>
                </StyledForm>
              )}
            </PaymentFormProvider>
          )}
        </FormSection>
      </PageWrapper>
    </>
  )
}

export default RegisterForm
